import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

import { ExportTable, FormatDate, SortByLatest } from '../../utils/Functions';
import Table from '../Table';
import InfoPanel from '../Panels/InfoPanel';
import InvoiceStatus from '../../data/InvoiceStatus';
import MiniButton from '../Buttons/MiniButton';
import IziToast from '../IziToast';
import iziResendInvoice from '../IziToast/ResendInvoice';
import PayModal from '../PayModal';
import ManualPaymentDetails from '../PayModal/ManualPaymentDetails';

const InvoiceTable = ({
  isLoading, error, invoices, onReload,
}) => {
  // life-cycle hooks
  const user = JSON.parse(window.localStorage.getItem('nyumbani'));
  const requestConfig = {
    headers: {
      Authorization: `Bearer ${user.token}`,
      'Content-Type': 'application/json',
    },
  };

  // ── Manual "Pay" + read-only viewer modal state ─────────────────────
  const [payRow, setPayRow] = useState(null);
  const [manualViewRow, setManualViewRow] = useState(null);

  const openPayModal = (invoice) => setPayRow(invoice);
  const closePayModal = () => setPayRow(null);
  const onPaySuccess = () => {
    setPayRow(null);
    if (onReload) onReload();
  };
  const buildPayFormData = ({
    row, amount, mode, transactionDetails, file,
  }) => {
    const fd = new FormData();
    fd.append('invoice_id', row.id);
    fd.append('amount', amount);
    fd.append('mode', mode);
    fd.append('transaction_details', transactionDetails || '');
    if (file) fd.append('proof', file);
    return fd;
  };

  useEffect(() => {
    ExportTable('invoices_table', 'invoices');
  }, [isLoading, invoices]);

  async function onSendInvoice(invoice) {
    const SMSbody = {
      id: invoice.id,
      send_sms: true,
      invoice_type: 'rent',
      changelog: [{
        user: {
          id: user.id,
          type: user.type,
        },
        action: 'Re-sending invoice via SMS',
        timestamp: new Date().getTime(),
      }],
    };

    const Emailbody = {
      id: invoice.id,
      send_email: true,
      invoice_type: 'rent',
      changelog: [{
        user: {
          id: user.id,
          type: user.type,
        },
        action: 'Re-sending invoice via Email',
        timestamp: new Date().getTime(),
      }],
    };

    const WhatsAppbody = {
      id: invoice.id,
      send_whatsapp: true,
      invoice_type: 'rent',
      changelog: [{
        user: {
          id: user.id,
          type: user.type,
        },
        action: 'Re-sending invoice via WhatsApp',
        timestamp: new Date().getTime(),
      }],
    };

    iziResendInvoice(
      'Resend this invoice as ?',
      () => {
        resendInvoiceAsSMS(SMSbody, requestConfig);
      },
      () => {
        resendInvoiceASEmail(Emailbody, requestConfig);
      },
      () => {
        resendInvoiceAsWhatsApp(WhatsAppbody, requestConfig);
      },
      undefined,
    );
  }

  async function resendInvoiceAsSMS(body, requestConfig) {
    const {
      data,
    } = await axios.put(
      `${process.env.REACT_APP_API_URL}invoices`,
      body,
      requestConfig,
    );
    if (data) {
      IziToast('Successfully sent an Invoice SMS', 'green');
    } else {
      IziToast('Error sending Invoice SMS', 'red');
    }
  }

  async function resendInvoiceASEmail(body, requestConfig) {
    const {
      data,
    } = await axios.put(
      `${process.env.REACT_APP_API_URL}invoices`,
      body,
      requestConfig,
    );
    if (data) {
      IziToast('Successfully sent an Invoice Email', 'green');
    } else {
      IziToast('Error sending Invoice Email', 'red');
    }
  }

  async function resendInvoiceAsWhatsApp(body, requestConfig) {
    const {
      data,
    } = await axios.put(
      `${process.env.REACT_APP_API_URL}invoices`,
      body,
      requestConfig,
    );
    if (data) {
      IziToast('Successfully sent an Invoice on WhatsApp', 'green');
    } else {
      IziToast('Error sending Invoice on WhatsApp', 'red');
    }
  }

  const canPay = user.type === 'admin' || user.type === 'landlord';

  return isLoading ? (
    <InfoPanel message="Loading..." />
  ) : error ? (
    <InfoPanel message="An error occurred" hasError />
  ) : (
    <>
      <PayModal
        row={payRow}
        title="Pay Invoice"
        subtitle={payRow ? `INV-${String(payRow.id).substring(0, 10).toUpperCase()}` : ''}
        maxAmount={payRow ? Number(payRow.balance) : null}
        endpoint={`${process.env.REACT_APP_API_URL}payments/manual`}
        requestConfig={requestConfig}
        buildFormData={buildPayFormData}
        onClose={closePayModal}
        onSuccess={onPaySuccess}
      />
      <ManualPaymentDetails
        row={manualViewRow}
        amount={manualViewRow ? manualViewRow.total_amount : null}
        onClose={() => setManualViewRow(null)}
      />
      {invoices && invoices.length ? (
        <Table tableId="invoices_table" marginTop={10}>
          { JSON.parse(window.localStorage.getItem('nyumbani')).type == 'admin' ? (
            <thead>
              <tr>
                <th>No.</th>
                <th>Invoice Date</th>
                <th>Property</th>
                <th>Unit No.</th>
                <th>Name</th>
                <th>Amount</th>
                <th>Invoice No.</th>
                <th>Particular</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
          )
            : (
              <thead>
                <tr>
                  <th>No.</th>
                  <th>Invoice Date</th>
                  <th>Property</th>
                  <th>Unit No.</th>
                  <th>Name</th>
                  <th>Amount</th>
                  <th>Invoice No.</th>
                  <th>Particular</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
            )}
          <tbody>
            {SortByLatest(invoices).map((invoice, id) => {
              let index = id;
              let propertyName = '';
              let unitNumber = '';
              let tenantName = '';
              let particular = '-';
              if (invoice.reference_data) {
                const { reference_data: referenceData } = invoice;
                if (referenceData) {
                  particular = referenceData.info;
                }
              }

              const invoiceNo = `INV-${String(invoice.id)
                .substring(0, 10)
                .toUpperCase()}`;

              if (invoice.property) {
                const { property } = invoice;
                if (property) {
                  propertyName = property.name;
                }
              }

              if (invoice.tenant) {
                const { tenant } = invoice;
                if (tenant) {
                  tenantName = `${tenant.first_name} ${tenant.last_name}`;
                  if (tenant.other_details) {
                    unitNumber = tenant.other_details.unit_number || '';
                  }
                }
              }

              return (
                JSON.parse(window.localStorage.getItem('nyumbani')).type === 'admin' ? (
                  <tr key={invoice.id}>
                    <td>{++index}</td>
                    <td>{FormatDate(invoice.invoice_date)}</td>
                    <td>{propertyName}</td>
                    <td>{unitNumber}</td>
                    <td>{tenantName}</td>
                    <td>{invoice.total_amount}</td>
                    <td>{invoiceNo}</td>
                    <td>{particular}</td>
                    <td>{InvoiceStatus(invoice.status) || ''}</td>
                    <td>
                      <MiniButton
                        caption="Resend Invoice"
                        onClick={() => {
                          onSendInvoice(invoice);
                        }}
                        buttonType="danger"
                      />
                      {canPay && Number(invoice.balance) > 0 && invoice.status !== 'cancelled' && (
                        <button
                          type="button"
                          className="btn btn-xs"
                          title="Record a manual payment for this invoice"
                          style={{ color: '#27ae60', minWidth: 50, marginLeft: 2 }}
                          onClick={() => openPayModal(invoice)}
                        >
                          <i className="fa fa-money" style={{ marginRight: 4 }} />
                          Pay
                        </button>
                      )}
                      {invoice.status === 'paid' && invoice.payment_channel === 'manual' && (
                        <button
                          type="button"
                          className="btn btn-xs"
                          title="View manual payment details"
                          style={{ color: '#2980b9', minWidth: 50, marginLeft: 2 }}
                          onClick={() => setManualViewRow(invoice)}
                        >
                          <i className="fa fa-eye" style={{ marginRight: 4 }} />
                          Manual
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-xs"
                        title="View PDF invoice"
                        style={{
                          color: '#d73836',
                          minWidth: 50,
                          marginLeft: 2,
                        }}
                        onClick={() => {
                          const ref = `INV-${String(invoice.id).substring(0, 10).toUpperCase()}`;
                          const base = (process.env.REACT_APP_API_URL || '').replace(/\/?$/, '');
                          window.open(`${base}/public/invoice.pdf?ref=${ref}`, '_blank');
                        }}
                      >
                        <i className="fa fa-file-pdf-o" style={{ marginRight: 4 }} />
                        PDF
                      </button>
                    </td>
                  </tr>
                )
                  : (
                    <tr key={invoice.id}>
                      <td>{++index}</td>
                      <td>{FormatDate(invoice.invoice_date)}</td>
                      <td>{propertyName}</td>
                      <td>{unitNumber}</td>
                      <td>{tenantName}</td>
                      <td>{invoice.total_amount}</td>
                      <td>{invoiceNo}</td>
                      <td>{particular}</td>
                      <td>{InvoiceStatus(invoice.status) || ''}</td>
                      <td>
                        {canPay && Number(invoice.balance) > 0 && invoice.status !== 'cancelled' && (
                          <button
                            type="button"
                            className="btn btn-xs"
                            title="Record a manual payment for this invoice"
                            style={{ color: '#27ae60', minWidth: 50 }}
                            onClick={() => openPayModal(invoice)}
                          >
                            <i className="fa fa-money" style={{ marginRight: 4 }} />
                            Pay
                          </button>
                        )}
                        {invoice.status === 'paid' && invoice.payment_channel === 'manual' && (
                          <button
                            type="button"
                            className="btn btn-xs"
                            title="View manual payment details"
                            style={{ color: '#2980b9', minWidth: 50, marginLeft: 2 }}
                            onClick={() => setManualViewRow(invoice)}
                          >
                            <i className="fa fa-eye" style={{ marginRight: 4 }} />
                            Manual
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-xs"
                          title="View PDF invoice"
                          style={{
                            color: '#d73836',
                            minWidth: 50,
                            marginLeft: 2,
                          }}
                          onClick={() => {
                            const ref = `INV-${String(invoice.id).substring(0, 10).toUpperCase()}`;
                            const base = (process.env.REACT_APP_API_URL || '').replace(/\/?$/, '');
                            window.open(`${base}/public/invoice.pdf?ref=${ref}`, '_blank');
                          }}
                        >
                          <i className="fa fa-file-pdf-o" style={{ marginRight: 4 }} />
                          PDF
                        </button>
                      </td>
                    </tr>
                  )
              );
            })}
          </tbody>
        </Table>
      ) : (
        <InfoPanel message="Seems no invoices found" />
      )}
    </>
  );
};

InvoiceTable.propTypes = {
  invoices: PropTypes.oneOfType([PropTypes.array, PropTypes.object]).isRequired,
  isLoading: PropTypes.bool.isRequired,
  error: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  onReload: PropTypes.func,
};

InvoiceTable.defaultProps = {
  error: null,
  onReload: null,
};

export default InvoiceTable;
